// Thumbs for a tip, an answer or a summary (docs/design/CALL-POP-KRISP-BAR.md lane 6). Two inline-SVG buttons in a
// fixed box one header line high (40 by 16 px): the voted one is aria-pressed and drawn in brass with a tinted fill,
// and nothing else changes, so a vote never moves anything. Until there is something to rate (no target id) the two
// buttons keep their places but are not shown. The view supplies the vote and the handler; feedbackClient.ts keeps the
// votes and sends them. The say header's instance (target 'tip') is the 'tip-feedback' slot; the ones on answer rows and
// in the wrap-up add no slot. CallWindowStyles concatenates TIP_FEEDBACK_CSS.
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';

export type TipFeedbackVote = 'up' | 'down';

export interface TipFeedbackProps {
  target: 'tip' | 'answer' | 'summary';
  targetId: string | null;
  onVote(vote: TipFeedbackVote): void;
  voted: TipFeedbackVote | null;
}

/** What each button says to a screen reader and in its tooltip, by what is being rated. */
export function tipFeedbackLabel(target: TipFeedbackProps['target'], vote: TipFeedbackVote): string {
  return `${vote === 'up' ? 'Helpful' : 'Not helpful'} ${target}`;
}

/** Two 16 px buttons and the gap between them, one 16 px header line high. The icons are 1em of the header's 12 px.
 * Hover, pressed and focus change colour, fill and outline only; hidden buttons keep their boxes. */
export const TIP_FEEDBACK_CSS = `
.cw-tipfb { flex: none; box-sizing: border-box; width: calc(2 * 16px + var(--space-2)); height: 16px; display: inline-flex; align-items: center; justify-content: flex-end; gap: var(--space-2); overflow: hidden; }
.cw-tipfb-btn { flex: none; box-sizing: border-box; width: 16px; height: 16px; margin: 0; padding: 0; border: 0; border-radius: var(--radius-control); background: none; color: var(--ink-3); font-size: var(--text-min); line-height: 0; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.cw-tipfb-btn > svg { flex: none; }
.cw-tipfb-btn:hover { color: var(--ink-2); }
.cw-tipfb-btn[aria-pressed='true'] { color: var(--brass); }
.cw-tipfb-btn[aria-pressed='true'] > svg { fill: var(--brass-tint); }
.cw-tipfb-btn:focus-visible { outline: 2px solid var(--focus); outline-offset: -2px; }
.cw-tipfb-btn:disabled { visibility: hidden; cursor: default; }
@media (forced-colors: active) { .cw-tipfb-btn[aria-pressed='true'] { color: Highlight; } .cw-tipfb-btn[aria-pressed='true'] > svg { fill: Highlight; } }
`;

export function TipFeedback({ target, targetId, onVote, voted }: TipFeedbackProps) {
  const ready = !!targetId;
  const button = (vote: TipFeedbackVote) => {
    const label = tipFeedbackLabel(target, vote);
    return (
      <button type="button" className="cw-tipfb-btn" data-vote={vote} aria-pressed={ready && voted === vote} disabled={!ready}
        aria-label={label} title={label} onClick={() => onVote(vote)}>
        <Icon icon={vote === 'up' ? ThumbsUp : ThumbsDown} size="1em" />
      </button>
    );
  };
  return (
    <span className="cw-tipfb" data-slot={target === 'tip' ? 'tip-feedback' : undefined} data-target={target} role="group" aria-label={`Rate this ${target}`}>
      {button('up')}
      {button('down')}
    </span>
  );
}
